import Paper from '@mui/material/Paper/Paper'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import React, { useMemo } from 'react'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'
import { KnockStatusChip } from '@user/features/MapView/KnockStatusChip'
import { StatusChip } from '@user/features/MapView/StatusChip'
import { Theme } from '@mui/material/styles/createTheme'
import { SxProps } from '@mui/material/styles'
import { AccessTime } from '@workwave-tidal/core/icons/AccessTime'
import { formatKnockStatusCreatedInfo } from '@user/utils/knockStatusFormatting'
import { EditableKnockStatusReason } from '@user/features/MapView/KnockStatusModal/EditableKnockStatusReason'

type Props = {
  sx?: SxProps<Theme>
  knockStatus?: {
    id?: number | null
    isDefault: boolean
    status: string
    note: string | null
    createdTime?: string | Date | null
    createdByName?: string | null
  }
  status?: string
  chipMarginTop?: boolean
  hideCreatedInfo?: boolean
  viewHistoryButton?: React.ReactNode
  /** Rendered at the top-right of the KNOCK STATUS header (e.g. the clear trash icon). */
  clearButton?: React.ReactNode
  canEdit?: boolean
  isSavingReason?: boolean
  onSaveReason?: (newReason: string | null) => Promise<boolean>
}

function hasMeaningfulKnockStatus(
  knockStatus: Props['knockStatus'],
): knockStatus is NonNullable<Props['knockStatus']> {
  if (!knockStatus) return false
  const s = knockStatus.status?.trim()
  return !!s && s !== 'None'
}

export function StatusSection(props: Props) {
  const {
    sx,
    knockStatus,
    status,
    chipMarginTop,
    hideCreatedInfo,
    viewHistoryButton,
    clearButton,
    canEdit,
    isSavingReason,
    onSaveReason,
  } = props
  const isD2dEnabled = useD2DKnockEnabled()

  const showStatus = !!status
  const knockChipProps = useMemo(() => {
    if (!hasMeaningfulKnockStatus(knockStatus)) {
      return null
    }
    return {
      status: knockStatus.status,
      isDefault: knockStatus.isDefault,
    }
  }, [knockStatus])

  const reasonText = useMemo(() => {
    const n = knockStatus?.note?.trim()
    return n || null
  }, [knockStatus?.note])

  const createdInfoText = useMemo(() => {
    return formatKnockStatusCreatedInfo(
      knockStatus?.createdTime,
      knockStatus?.createdByName,
    )
  }, [knockStatus?.createdTime, knockStatus?.createdByName])

  if (!isD2dEnabled && !showStatus) {
    return null
  }

  return (
    <>
      {showStatus ? (
        <Typography
          variant="subtitle4"
          sx={{
            display: 'flex',
            gap: 0.8,
            alignItems: 'center',
            flexWrap: 'wrap',
            mt: chipMarginTop ? 1 : 0,
          }}
        >
          <StatusChip status={status} />
        </Typography>
      ) : null}

      {isD2dEnabled && (
        <Paper
          variant="outlined"
          sx={{
            px: 1.6,
            pt: 1.2,
            pb: 1.2,
            display: 'flex',
            flexDirection: 'column',
            mt: '15px',
            ...sx,
          }}
        >
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 400,
                  letterSpacing: '0.4px',
                  textTransform: 'uppercase',
                }}
              >
                Knock status
              </Typography>
              {clearButton}
            </Box>
            <Box
              sx={{
                mt: 0.6,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.8,
              }}
            >
              {knockChipProps?.status ? (
                <KnockStatusChip
                  status={knockChipProps.status}
                  isDefault={knockChipProps.isDefault}
                />
              ) : (
                <Typography variant="body2">-</Typography>
              )}
            </Box>

            <EditableKnockStatusReason
              reason={reasonText}
              canEdit={Boolean(canEdit && knockChipProps && knockStatus?.id)}
              isSaving={Boolean(isSavingReason)}
              onSave={onSaveReason ?? (async () => false)}
            />

            {viewHistoryButton && (
              <Box sx={{ mt: 0.4, ml: -1 }}>{viewHistoryButton}</Box>
            )}

            {!hideCreatedInfo && createdInfoText && (
              <Typography
                variant="body2"
                sx={{
                  mt: 0.6,
                  color: '#0E283FB2',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <AccessTime fontSize="small" />
                {'  '}
                {createdInfoText}
              </Typography>
            )}
          </Box>
        </Paper>
      )}
    </>
  )
}
